/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, {useCallback, useEffect, useMemo} from 'react'
import clsx from 'clsx'
import {Controller, useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import UiFormField from 'components/Forms/UiFormField'
import UiSelectWithGraphics from 'components/Ui/UiSelectWithGraphics'
import CompanyFormInput from 'pages/Front/Booking/components/CompanyForm/CompanyFormInput'
import {formatStreetLineFromAddress} from 'services/googleAddressService'
import UiLocationSearch from 'components/Ui/UiLocationSearch'
import ReservationDetailCountry from 'pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/ReservationDetailCountry'
import {getEnglishCountryNameFromAnyCountryValue} from 'utils/hooks/useGetCountryCode'
import {getCantonSelectOptions} from 'utils/cantonUtils'
import {PhoneCountry} from 'utils/countryUtils'
import {LeadBasicInputs} from 'components/leads/schemas/useLeadBasicSchema'

interface AddressOption {
    value: number
    label: string
    description: string
}

interface LeadBasicFormAddressSectionProps {
    compact: boolean
    hasExistingCompanyAddresses: boolean
    companyAddressOptions: AddressOption[]
    selectedCompanyAddressId: number | null
    selectedCountryCode: string
    setSelectedCountryCode: (code: string) => void
    orderedCountryOptions: PhoneCountry[]
    findCountryOption: (value?: string) => PhoneCountry | null
    isSwissCountryInput: boolean
    hasAddressField: boolean
    highlightMissingContact: boolean
    testField: (field: string) => boolean
    countryInput: string
    handleCompanyAddressSelect: (addressId: string | number | null) => void
    handleCreateNewAddress: () => void
}

const LeadBasicFormAddressSection: React.FC<LeadBasicFormAddressSectionProps> = ({
    compact,
    hasExistingCompanyAddresses,
    companyAddressOptions,
    selectedCompanyAddressId,
    selectedCountryCode,
    setSelectedCountryCode,
    orderedCountryOptions,
    findCountryOption,
    isSwissCountryInput,
    hasAddressField,
    highlightMissingContact,
    testField,
    countryInput,
    handleCompanyAddressSelect,
    handleCreateNewAddress,
}) => {
    const {t} = useTranslation()
    const {register, control, formState: {errors}, setValue, getValues} = useFormContext<LeadBasicInputs>()

    const cantonOptions = useMemo(
        () => getCantonSelectOptions(selectedCountryCode),
        [selectedCountryCode]
    )

    const normalizeCantonValue = useCallback(
        (value?: string | null) => {
            const raw = (value || '').toString().trim()
            if (!raw) {
                return ''
            }

            const normalizedRaw = raw
                .toLowerCase()
                .replace(/\bsankt\b/g, 'st.')
                .replace(/\s+/g, ' ')

            const byCode = cantonOptions.find(
                (option) => option.value.toLowerCase() === raw.toLowerCase()
            )
            if (byCode) {
                return byCode.value
            }

            const byLabel = cantonOptions.find((option) => {
                const label = option.label.toLowerCase().replace(/\s+/g, ' ')
                return (
                    label === normalizedRaw ||
                    label === raw.toLowerCase() ||
                    label.startsWith(`${normalizedRaw} `) ||
                    label.startsWith(`${normalizedRaw}(`) ||
                    label.startsWith(`${raw.toLowerCase()} `) ||
                    label.startsWith(`${raw.toLowerCase()}(`)
                )
            })
            if (byLabel) {
                return byLabel.value
            }

            const codeInParentheses = raw.match(/\(([A-Za-z\u00c0-\u017f]+)\)\s*$/)?.[1]
            if (codeInParentheses) {
                const byParsedCode = cantonOptions.find(
                    (option) => option.value.toLowerCase() === codeInParentheses.toLowerCase()
                )
                if (byParsedCode) {
                    return byParsedCode.value
                }
            }

            return raw
        },
        [cantonOptions]
    )

    useEffect(() => {
        if (!['CH', 'AT', 'DE', 'LI'].includes(selectedCountryCode) || cantonOptions.length === 0) {
            return
        }

        const currentState = getValues('state')
        const normalizedState = normalizeCantonValue(currentState)

        if ((currentState || '') !== normalizedState) {
            setValue('state', normalizedState, {shouldDirty: false, shouldValidate: false})
        }
    }, [selectedCountryCode, cantonOptions, getValues, setValue, normalizeCantonValue])

    return (
        <UiFormInner title={t('Account Address')} className={clsx('LeadBasicFormAddressSection h-full lg:order-4')}>
            <div className={clsx(highlightMissingContact && 'rounded-md ring-1 ring-red-400/60 p-2')}>
                <UiFormGrid cols={2} gap={compact ? 3 : 3} responsive={false} className={clsx('grid-cols-2')}>
					{selectedCompanyAddressId && <input type={"hidden"} name={"address_id"} value={selectedCompanyAddressId} /> }
                    {hasExistingCompanyAddresses && (
                        <UiFormField className={clsx('col-span-2')}>
                            <div className="flex items-end gap-2">
                                <UiSelectWithGraphics
                                    className="flex-1"
                                    label={t('Address')}
                                    selected={selectedCompanyAddressId}
                                    setSelected={handleCompanyAddressSelect}
                                    options={companyAddressOptions}
                                />
                                <button
                                    type="button"
                                    className="h-10 px-3 rounded-md border border-gray-300 dark:border-gray-700 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900"
                                    onClick={handleCreateNewAddress}
                                >
                                    {t('New address')}
                                </button>
                            </div>
                        </UiFormField>
                    )}

                    <UiLocationSearch
                        className={clsx('col-span-2')}
                        name="address_finder"
                        label={t('Find address')}
                        placeholder={t('Search address...')}
                        helperText={t('Select a street address to autofill the fields below')}
                        countryCode={selectedCountryCode}
                        onSelect={(address) => {
                            setValue('street_address', formatStreetLineFromAddress(address), {
                                shouldDirty: true,
                                shouldValidate: true,
                            })
                            setValue('city', address.city, {shouldDirty: true, shouldValidate: true})
                            setValue('postal_code', address.postcode, {shouldDirty: true, shouldValidate: true})

                            const regionValue = address.region || address.city
                            if (regionValue) {
                                const normalizedRegion = normalizeCantonValue(regionValue)
                                const matchedCanton = cantonOptions.some(
                                    (option) => option.value === normalizedRegion
                                )
                                if (matchedCanton || address.region) {
                                    setValue('state', normalizedRegion, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    })
                                }
                            }

                            const countryCode = address.country_code?.toUpperCase()
                            let matchedCountry = countryCode
                                ? orderedCountryOptions.find((option) => option.textCode === countryCode)
                                : undefined

                            if (!matchedCountry && address.country) {
                                matchedCountry = orderedCountryOptions.find(
                                    (option) =>
                                        option.name.toLowerCase() === address.country.toLowerCase() ||
                                        option.textCode.toLowerCase() === address.country.toLowerCase()
                                )
                            }

                            if (matchedCountry?.textCode) {
                                setSelectedCountryCode(matchedCountry.textCode)
                                void getEnglishCountryNameFromAnyCountryValue(
                                    matchedCountry.name,
                                    'Switzerland'
                                ).then((normalizedCountry) => {
                                    setValue('country', normalizedCountry, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    })
                                })
                            } else if (address.country) {
                                void getEnglishCountryNameFromAnyCountryValue(
                                    address.country,
                                    'Switzerland'
                                ).then((normalizedCountry) => {
                                    setValue('country', normalizedCountry, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    })
                                })
                            }
                        }}
                    />

                    <CompanyFormInput
                        className={clsx('col-span-1')}
                        label={t('Street address')}
                        name="street_address"
                        register={register}
                        errors={errors}
                        required={testField('street_address')}
                        placeholder={t('Enter street address')}
                        autoComplete="one-time-code"
                    />

                    <CompanyFormInput
                        className={clsx('col-span-1')}
                        label={t('Postal code')}
                        name="postal_code"
                        register={register}
                        errors={errors}
                        placeholder={t('Enter postal code')}
                        autoComplete="one-time-code"
                    />

                    <CompanyFormInput
                        className={clsx('col-span-1')}
                        label={t('City')}
                        name="city"
                        register={register}
                        errors={errors}
                        placeholder={t('Enter city')}
                        autoComplete="one-time-code"
                    />

                    {['CH', 'AT', 'DE', 'LI'].includes(selectedCountryCode) ? (
                        <Controller
                            control={control}
                            name="state"
                            render={({field}) => (
                                <div className={clsx('col-span-1')}>
                                    <UiSelectWithGraphics
                                        label={t('Kanton')}
                                        selected={normalizeCantonValue(field.value || '')}
                                        setSelected={field.onChange}
                                        options={cantonOptions}
                                        debounceMs={0}
                                    />
                                    {errors.state?.message && (
                                        <div className="mt-1 text-xs text-red-500">
                                            {String(errors.state.message)}
                                        </div>
                                    )}
                                </div>
                            )}
                        />
                    ) : (
                        <CompanyFormInput
                            className={clsx('col-span-1')}
                            label={t('Kanton')}
                            name="state"
                            register={register}
                            errors={errors}
                            required={isSwissCountryInput && hasAddressField}
                            placeholder={t('Enter region or state')}
                            autoComplete="one-time-code"
                        />
                    )}

                    <ReservationDetailCountry
                        className={clsx('col-span-1')}
                        options={orderedCountryOptions}
                        defaultCountry={selectedCountryCode}
                        value={countryInput || ''}
                        register={register}
                        name="country"
                        label={t('Country')}
                        setValue={(name: string, value: string) => {
                            const matchedCountry = findCountryOption(value)
                            if (matchedCountry?.textCode) {
                                setSelectedCountryCode(matchedCountry.textCode)
                            }
                            getEnglishCountryNameFromAnyCountryValue(value, 'Switzerland').then(
                                (normalizedCountry) => {
                                    setValue(name as keyof LeadBasicInputs, normalizedCountry, {shouldDirty: true})
                                }
                            )
                        }}
                        errors={errors}
                        required={testField('country')}
                    />
                </UiFormGrid>
            </div>
        </UiFormInner>
    )
}

export default LeadBasicFormAddressSection
